import type { FieldValues, AnuraResponse, AnuraRequest, TrackingParams } from '@/types';

import { v4 as uuidv4 } from 'uuid';
import EF from '@everflow/everflow-sdk';
import { loadScript } from '@/utils/scriptLoader';

import { FormInitialValues } from '@/constants';
import { fetchTIH, loadGTM, isGtmLoaded } from '@/utils/analytics';
import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';
import { FeatheryFieldTypes } from '@feathery/react/dist/utils/init';
import Field from '@feathery/react/dist/utils/entities/Field';
import { TimeoutError, FeatheryError } from '@/types/errors';
import { initializeRedditPixel } from '@/utils/redditPixel';

EF.configure({
	tracking_domain: 'https://www.tf9jthtrk.com',
});

export const checkFeatheryElements = () => {
	const featheryElements = document.querySelectorAll('[data-feathery-id]');
	if (featheryElements.length === 0) {
		Sentry.withScope((scope) => {
			scope.setTag('api', 'feathery');
			scope.setTag('error_type', 'missing_elements');
			scope.setLevel('error');
			Sentry.captureException(new FeatheryError('Feathery form elements not found'));
		});
	}
};

const setAndTriggerEfid = (efid: string) => {
	window.dataLayer = window.dataLayer || [];

	window.dataLayer?.push({
		var_efid: efid,
	});
	window.dataLayer?.push({
		event: 'trigger_efid',
	});

	posthog.capture('trigger_efid', {
		efid,
	});
};

export const getSubstringBetweenQuotes = (str: string): string => {
	const subString = str.substring(str.indexOf("'") + 1, str.lastIndexOf("'"));

	return subString;
};
const getParams = (fieldValues: FieldValues, params: URLSearchParams) => {
	params.forEach((value, key) => {
		if (key === 'pageurl') {
			if (!params.get(key)) {
				return;
			}
		}
		fieldValues[key] = value || '';
	});

	return fieldValues;
};

export const everflowClickTracking = async (
	fieldValues: FieldValues,
	offerId: string,
	affId: string,
) => {
	if (offerId === null || affId === null) {
		return;
	}

	try {
		const clickPromise = EF.click({
			offer_id: offerId,
			affiliate_id: affId,
			sub1: EF.urlParameter('sub1'),
			sub2: EF.urlParameter('sub2'),
			sub3: EF.urlParameter('sub3'),
			sub4: EF.urlParameter('sub4'),
			sub5: EF.urlParameter('sub5'),
			uid: EF.urlParameter('uid'),
			source_id: EF.urlParameter('source_id'),
			parameters: {
				url: window.location.href,
			},
		});

		const timeoutPromise = new Promise((_, reject) => {
			setTimeout(() => reject(new TimeoutError('EF.click failed to respond')), 5000);
		});

		const transactionId = await Promise.race([clickPromise, timeoutPromise]);

		fieldValues.efid = transactionId;
		setAndTriggerEfid(transactionId);
	} catch (err) {
		// eslint-disable-next-line no-console
		console.error(err);
		Sentry.withScope((scope) => {
			scope.setTag('api', 'EF.click');
			scope.setTag('offerId', offerId);
			scope.setTag('affiliateId', affId);
			scope.setLevel('error');
			Sentry.captureException(err);
		});
	}
};

const getResponses = async (
	fieldValues: FieldValues,
	params: URLSearchParams,
	url: string,
	trackingParams: TrackingParams,
) => {
	// total injury help landing
	const data = await fetchTIH(params, url);

	const efid = params.get('efid');
	const oid = params.get('oid');
	const aid = params.get('aid');
	const ef_offer_id = params.get('ef_offer_id');
	const ef_affiliate_id = params.get('ef_affiliate_id');
	const landing = data.landing;

	const { gclid, wbraid, gbraid } = trackingParams;

	if (gclid || wbraid || gbraid) {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'tracking_parameters',
			...trackingParams,
		});
	}

	posthog.setPersonProperties({
		OID: oid || landing?.cid,
		AID: aid || landing?.aid,
	});

	if (efid) {
		setAndTriggerEfid(efid);
	} else if (ef_offer_id && ef_affiliate_id) {
		await everflowClickTracking(fieldValues, ef_offer_id, ef_affiliate_id);
	} else if (oid && aid) {
		// Prioritize OID and AID URL parameters for tracking
		await everflowClickTracking(fieldValues, oid, aid);
	} else if (landing?.cid && landing?.aid) {
		// Fall back to landing page tracking if OID and AID are missing
		await everflowClickTracking(fieldValues, landing.cid, landing.aid);
	}

	if (data.landing?.gtm_tag) {
		if (!isGtmLoaded()) {
			loadGTM(data.landing.gtm_tag);
		}
	}

	if (data.landing?.pixel) {
		if (typeof fbq === 'undefined') {
			const { default: ReactPixel } = await import('react-facebook-pixel');

			const options = {
				autoConfig: true,
				debug: false,
			};
			ReactPixel.init(data.landing.pixel, undefined, options);
			// Use ReactPixel.pageView() to get all default configurations
			ReactPixel.pageView();
			// Add tracking parameters to all events
			if (Object.values(trackingParams).some((value) => value !== '')) {
				window.fbq('track', 'ViewContent', trackingParams);
			}
		}
	}

	// connection_id
	fieldValues.connection_id = params.get('connection_id') || posthog.get_distinct_id() || uuidv4();

	// user_agent
	if (typeof window !== 'undefined') {
		fieldValues.ua = navigator.userAgent;
	}

	if (data.landing?.reddit_pixel) {
		await initializeRedditPixel(
			data.landing.reddit_pixel,
			{
				conversion_id: fieldValues.connection_id,
			},
			trackingParams,
		);
	}

	// anura
	const resultHandler = async (http: XMLHttpRequest) => {
		const response = JSON.parse(http.response);

		if (response.adblocker === 0) {
			const e = document.createElement('div');
			e.id = 'XMBsEyhmvnZt';
			e.style.display = 'none';
			document.body.appendChild(e);
		}
		const hidden = document.querySelector('#hidden-form-data');

		// attach the result to the form for processing on
		// submit: see trusted form.

		const i = document.createElement('input');
		i.type = 'hidden';
		i.name = 'anura_response';
		i.id = 'anura_response_0';
		i.value = response.result;
		hidden?.appendChild(i);
	};

	const anuraResponseHandler = async (response: AnuraResponse) => {
		if (!response.getId() && !response.getExId()) return;
		const method = 'POST';
		const _params = ['instance=1765050722'];

		if (response.getId()) {
			_params.push(`id=${encodeURIComponent(response.getId())}`);
		}
		if (response.getExId()) {
			_params.push(`exid=${encodeURIComponent(response.getExId())}`);
		}
		const _url = 'https://script.anura.io/result.json';

		if (window.XMLHttpRequest) {
			const http = new XMLHttpRequest();

			if (http && 'withCredentials' in http) {
				http.open(method, _url, true);
				if (method === 'POST') {
					http.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
				}
				http.onload = function () {
					resultHandler(this);
				};
				http.send(method === 'POST' ? _params.join('&') : '');
			}
		}
	};

	const anuraSetup = () => {
		if (typeof window === 'undefined') {
			return;
		}

		window.anuraResponseHandler = anuraResponseHandler;

		const request: AnuraRequest = {
			instance: 1765050722,
			source: params.get('campaign_id'),
			campaign: params.get('s1'),
			additional: JSON.stringify({
				1: params.get('requestid'),
				2: params.get('s2'),
				3: params.get('s3'),
				4: params.get('s4'),
				5: params.get('s5'),
				6: params.get('affid'),
				7: params.get('cr'),
			}),
			callback: 'anuraResponseHandler',
		};

		const _params = [];
		for (const x in request) {
			_params.push(`${x}=${encodeURIComponent(request[x] || '')}`);
		}
		_params.push(Math.floor(1e12 * Math.random() + 1).toString());

		const anuraUrl = `https://script.anura.io/request.js?${_params.join('&')}`;

		loadScript(
			{
				src: anuraUrl,
				bundleId: 'anura-script',
			},
			'Anura',
		);
	};
	anuraSetup();

	return fieldValues;
};

export const removeSpecialChars = (str: string) => {
	return str
		.replace(/[\u{10000}-\u{10FFFF}]/gu, '') // remove 4 byte characters
		.replace(/[\n\r]/g, ' ') // remove newlines and replace with space
		.replace(/\\/g, ' '); // remove backslashes and replace with space
};

export const convertToJson = (fieldValues: Record<string, Field>) => {
	const data: Record<string, FeatheryFieldTypes> = {};

	for (const [key, value] of Object.entries(fieldValues)) {
		// don't include undefined,false, or null values but include 0
		if (!!value.value || value.value === 0) {
			if (typeof value.value === 'string') {
				data[key] = removeSpecialChars(value.value);
			} else {
				data[key] = value.value;
			}
		}
	}

	return data;
};

export const setHiddenFields = async (
	params: URLSearchParams,
	url: string,
	trackingParams: TrackingParams,
): Promise<FieldValues> => {
	const fieldValues = { ...FormInitialValues };

	// get the parameters and set them for submissison
	const withParams = getParams(fieldValues, params);

	// get responses from third party code or set them in the dom for Form.tsx
	const withParamsAndResponses = await getResponses(withParams, params, url, trackingParams);

	return withParamsAndResponses;
};
